import { mentionToMarkerText, toMention } from '@design/chat/mention-composer';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { MemoResearchMarkdown } from '@terminal/views/alfred/research/research-markdown/ResearchMarkdown';
import clsx from 'clsx';
import { Components } from 'react-markdown';
import styles from './MentionAwareText.module.scss';
import { ResolvedMentionChip } from './ResolvedMentionChip';
import { remarkMentions } from './remarkMentions';

export interface MentionAwareTextProps {
  text: string;
  className?: string;
}

interface MentionMarkdownProps {
  'data-mention-type'?: string;
  'data-mention-id'?: string;
  'data-mention-label'?: string;
}

// `react-markdown`'s `Components` map is keyed by `React.JSX.IntrinsicElements`,
// so `<mention>` has to be declared there for `components={{ mention: … }}` to
// type-check.
declare module 'react' {
  // biome-ignore lint/style/noNamespace: JSX augmentation requires a namespace.
  // biome-ignore lint/style/useNamingConvention: the namespace is named `JSX`.
  namespace JSX {
    interface IntrinsicElements {
      mention: MentionMarkdownProps;
    }
  }
}

const mentionFromProps = ({
  'data-mention-type': type = '',
  'data-mention-id': id = '',
  'data-mention-label': label = '',
}: MentionMarkdownProps) => toMention(type, id, label);

// `remarkMentions` always turns markers into `<mention>` nodes, so the flag-off
// path has to rebuild the marker syntax instead of leaving it to the default
// renderer (which would drop the trigger and print the bare label).
const MentionNode = (props: MentionMarkdownProps) => {
  const mentionsEnabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2103-alfred-mentions')
  );
  const mention = mentionFromProps(props);

  if (!mention) {
    return null;
  }

  return mentionsEnabled ? (
    <ResolvedMentionChip mention={mention} />
  ) : (
    <>{mentionToMarkerText(mention)}</>
  );
};

const MentionsComponents: Partial<Components> = {
  mention: MentionNode,
};

const REMARK_PLUGINS = [remarkMentions];

export const MentionAwareText = ({
  text,
  className,
}: MentionAwareTextProps) => {
  const inputAsMarkdown = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5111-input-as-markdown')
  );

  return (
    <MemoResearchMarkdown
      message={text}
      className={clsx(styles.mentionAwareText, className)}
      components={MentionsComponents}
      remarkPlugins={inputAsMarkdown ? REMARK_PLUGINS : undefined}
    />
  );
};
